import React, { useEffect, useMemo, useState } from 'react';
import { useV3Api } from '../../api/useV3Api';
import { errorText } from '../../api/errorText';
import { gapText } from '../../drift/findingModel';
import { filterRows, programKey, programRows } from '../../drift/softwareModel';
import { useTableView } from '../../drift/useTableView';
import ColumnHeads from './ColumnHeads';
import ConfirmDialog from '../ConfirmDialog';
import Portal from '../Portal';
import Bear from '../Bear';
import ShowCommand from '../ShowCommand';

const FACTS = 'v3/facts';
const MANUAL = 'v3/actions/manual';
const list = (v) => (Array.isArray(v) ? v : []);

const COLUMNS = [
  { key: 'application', label: 'Application', text: (r) => `${r.name} ${r.publisher} ${r.id}` },
  { key: 'version', label: 'Version', text: (r) => r.version },
  { key: 'finding', label: 'Finding', text: (r) => (r.findings.length ? r.findings.map((f) => gapText(f.gap)).join(', ') : 'none') },
  { key: 'scope', label: 'Scope', text: (r) => r.scope, options: true },
];

// The software of one machine, from the latest Get-InstalledPrograms result, and the two things a person can do about it from here: install a program
// or remove one installed copy. Both go through POST v3/actions/manual: the job is queued at once (after a confirmation naming the machine) and its
// id is kept on a plan in the actions list. Nothing reads the job back, so the list below is as the machine last reported it.
export default function SoftwareTab({ device, account, findings }) {
  const { get, post } = useV3Api();
  const name = device.hostname || device.deviceId;
  const [state, setState] = useState({ state: 'loading', programs: [] });
  const [thirdParty, setThirdParty] = useState(true);
  const [removing, setRemoving] = useState(null);                // the row waiting for the go-ahead
  const [bladeOpen, setBladeOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState(null);                  // { text } or { error }

  useEffect(() => {
    let cancelled = false;
    setState({ state: 'loading', programs: [] });
    get(FACTS, { account, device: name, capability: 'software' })
      .then((body) => {
        if (cancelled) return;
        const software = (((body && body.items) || [])[0] || {}).software;
        setState(software && software.results ? { state: 'ok', programs: list(software.results.programs), updatedUtc: software.updatedUtc } : { state: 'none', programs: [] });
      })
      .catch((ex) => { if (!cancelled) setState({ state: 'error', programs: [], error: errorText(ex) }); });
    return () => { cancelled = true; };
  }, [get, account, name]);

  const rows = useMemo(() => programRows(state.programs, findings), [state.programs, findings]);
  const base = useMemo(() => filterRows(rows, { thirdParty }), [rows, thirdParty]);
  const view = useTableView(base, COLUMNS);
  const shown = view.rows;

  // Queue the install or removal and say so, with the job's id. The queue is the end of it.
  const queue = async (action, program, label, extra = {}) => {
    setBusy(true);
    setOutcome(null);
    try {
      const body = await post(MANUAL, { account, device: name, action, program, ...extra });
      const job = body && body.plan && body.plan.steps && body.plan.steps.apply && body.plan.steps.apply.jobId;
      setOutcome({ text: `${action === 'install' ? 'Install' : 'Uninstall'} of ${label} queued${job ? ` (job ${job})` : ''}. It is in the actions list; the machine reports its software again later.` });
    } catch (ex) {
      setOutcome({ error: `${action === 'install' ? 'Install' : 'Uninstall'} of ${label} was not queued: ${errorText(ex)}` });
    } finally {
      setBusy(false);
      setRemoving(null);
    }
  };

  return (
    <div className="device-software">
      <div className="filter-bar toolbar">
        <label className="check"><input type="checkbox" checked={thirdParty} onChange={(e) => setThirdParty(e.target.checked)} />Third-party only</label>
        <span className="sub">{state.state === 'ok' ? `${shown.length} of ${rows.length} programs` : ''}</span>
        <button type="button" className="btn btn-primary btn-sm push-right" onClick={() => setBladeOpen(true)}>Add software</button>
      </div>
      {outcome && outcome.text && <div className="notice" role="status">{outcome.text}</div>}
      {outcome && outcome.error && <div className="field-error" role="alert">{outcome.error}</div>}
      {state.state === 'loading' && <div className="help" role="status">Loading...</div>}
      {state.state === 'error' && <div className="field-error" role="alert">{state.error}</div>}
      {state.state === 'none' && <div className="help">This machine has not reported its software yet.</div>}
      {state.state === 'ok' && base.length === 0 && <div className="help">No programs match.</div>}
      {base.length > 0 && (
        <div className="mini-wrap">
          <table className="mini software-table" aria-label="Installed software">
            <colgroup><col /><col style={{ width: 150 }} /><col style={{ width: 190 }} /><col style={{ width: 130 }} /><col style={{ width: 120 }} /></colgroup>
            <thead><ColumnHeads view={view} tail={1} /></thead>
            <tbody>
              {shown.length === 0 && <tr><td colSpan={5} className="empty">No programs match the column filters.</td></tr>}
              {shown.map((r) => (
                <tr key={r.key}>
                  <td><span className="strong">{r.name}</span><div className="sub" title={r.id || r.publisher}>{r.id || r.publisher}</div></td>
                  <td className="nowrap">{r.version}</td>
                  <td>{r.findings.length ? r.findings.map((f) => <span key={f.id} className="pill pill-amber">{gapText(f.gap)}</span>) : <span className="sub">none</span>}</td>
                  <td className="nowrap">{r.scope}</td>
                  <td className="nowrap">
                    <button type="button" className="btn btn-sm" aria-label={`Uninstall ${r.name}${r.version ? ` ${r.version}` : ''}`} disabled={busy} onClick={() => setRemoving(r)}>Uninstall</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="editor-actions"><ShowCommand route={FACTS} params={{ account, device: name, capability: 'software' }} label="PowerShell" /></div>

      {removing && (
        <ConfirmDialog title={`Uninstall ${removing.name}?`} confirmLabel="Uninstall" busy={busy} onCancel={() => setRemoving(null)}
          onConfirm={() => queue('uninstall', programKey({ id: removing.id, name: removing.name }), removing.name, { name: removing.name, version: removing.version })}>
          <p>{`${removing.name}${removing.version ? ` ${removing.version}` : ''} will be uninstalled for real from ${name}${removing.version ? ' (that one copy only)' : ''}.`}</p>
        </ConfirmDialog>
      )}
      {bladeOpen && <InstallBlade device={device} account={account} installed={rows} busy={busy} onInstall={(pkg) => queue('install', pkg.id, pkg.name)} onClose={() => setBladeOpen(false)} />}
    </div>
  );
}

// The blade on the right: search the winget catalog and install a program on this machine only. A program already on the machine says so.
function InstallBlade({ device, account, installed, busy, onInstall, onClose }) {
  const { get } = useV3Api();
  const [text, setText] = useState('');
  const [found, setFound] = useState({ state: 'idle', items: [] });
  const [confirming, setConfirming] = useState(null);
  const name = device.hostname || device.deviceId;

  useEffect(() => {
    const q = text.trim();
    if (q.length < 2) { setFound({ state: 'idle', items: [] }); return undefined; }
    let cancelled = false;
    setFound((f) => ({ ...f, state: 'loading' }));
    const timer = setTimeout(() => {
      get('v3/packages', { q })
        .then((r) => { if (!cancelled) setFound({ state: 'ok', items: list(r && r.items) }); })
        .catch((ex) => { if (!cancelled) setFound({ state: 'error', items: [], error: errorText(ex) }); });
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [get, text]);

  const have = (id) => installed.find((r) => r.id.toLowerCase() === String(id).toLowerCase());
  return (
    <Portal>
      <div className="blade-scrim" onClick={onClose} role="presentation" />
      <aside className="blade" role="dialog" aria-label="Add software">
        <header>
          <Bear name="kongi" size={44} />
          <div className="blade-title">
            <h2>Add software</h2>
            <div className="sub">{`Installs on ${name} only. Account ${account}.`}</div>
          </div>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose} aria-label="Close">Close</button>
        </header>
        <div className="blade-body">
          <input type="search" aria-label="Search software" placeholder="Search all software by name or keyword, for example storage explorer" value={text} onChange={(e) => setText(e.target.value)} />
          {found.state === 'idle' && <div className="help">Type at least two characters to search.</div>}
          {found.state === 'loading' && <div className="help" role="status">Loading...</div>}
          {found.state === 'error' && <div className="field-error" role="alert">{found.error}</div>}
          {found.state === 'ok' && found.items.length === 0 && <div className="help">No software matches.</div>}
          {found.items.length > 0 && (
            <table aria-label="Software packages">
              <thead><tr><th>Software</th><th>Newest</th><th /></tr></thead>
              <tbody>
                {found.items.map((pkg) => {
                  const there = have(pkg.id);
                  return (
                    <tr key={pkg.id}>
                      <td><strong>{pkg.name}</strong><div className="help">{pkg.id}</div>{there && <span className="pill pill-green" data-testid="already-installed">{`Already installed${there.version ? ` (version ${there.version})` : ''}`}</span>}</td>
                      <td>{pkg.latestVersion}</td>
                      <td><button type="button" className="btn btn-primary btn-sm" aria-label={`Install ${pkg.name}`} disabled={busy || !!there} onClick={() => setConfirming(pkg)}>Install</button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </aside>
      {confirming && (
        <ConfirmDialog title={`Install ${confirming.name}?`} confirmLabel="Install" busy={busy} onCancel={() => setConfirming(null)}
          onConfirm={async () => { await onInstall(confirming); setConfirming(null); onClose(); }}>
          <p>{`It will be installed for real on ${name}.`}</p>
        </ConfirmDialog>
      )}
    </Portal>
  );
}
